/**
 * Chevron - Small reusable SVG chevron component
 * Factory function following the app's architectural patterns
 */

import { isPrimaryTouchDevice } from "@utils/device.js";

export type ChevronOrientation = "down" | "up" | "left" | "right";

export interface ChevronConfig {
  readonly orientation?: ChevronOrientation;
  readonly width?: number; // SVG width in px
  readonly height?: number; // SVG height in px
  readonly strokeWidth?: number;
  readonly className?: string; // Applied to container
  readonly svgClassName?: string; // Applied to svg element
  readonly onClick?: () => void;
}

export interface ChevronAPI {
  readonly getElement: () => HTMLElement;
  readonly setOrientation: (orientation: ChevronOrientation) => void;
  readonly setSize: (width: number, height: number) => void;
  readonly destroy: () => void;
}

/**
 * Creates a small chevron icon wrapped in a container.
 * Defaults match existing dropdown chevrons used in UI controls.
 */
export const createChevron = (config: ChevronConfig = {}): ChevronAPI => {
  const {
    orientation = "down",
    width = 9,
    height = 6,
    strokeWidth = 1,
    className = "dropdown-container",
    svgClassName = "dropdown-chevron",
    onClick,
  } = config;

  let isDestroyed = false;
  let currentOrientation: ChevronOrientation = orientation;
  let currentWidth = width;
  let currentHeight = height;

  // Container element
  const container = document.createElement("div");
  const isTouchDevice = isPrimaryTouchDevice();
  container.className = `${className}${
    isTouchDevice ? " touch-device" : ""
  }`.trim();

  // SVG element
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");

  const applySize = (): void => {
    svg.setAttribute("width", String(currentWidth));
    svg.setAttribute("height", String(currentHeight));
    // Use a consistent viewBox that matches the base path dimensions for down chevron
    svg.setAttribute("viewBox", "0 0 7 3.5");
  };

  const applyPath = (): void => {
    // Base "down" chevron path within 7x3.5 box
    path.setAttribute("d", "M0.5 0.5 L3.5 3 L6.5 0.5");
    path.setAttribute("stroke", "currentColor");
    path.setAttribute("stroke-width", String(strokeWidth));
    path.setAttribute("fill", "none");
    path.setAttribute("stroke-linecap", "round");
    path.setAttribute("stroke-linejoin", "round");

    // Reset transform then rotate as needed around center (3.5, 1.75)
    const centerX = 3.5;
    const centerY = 1.75;
    let rotate = 0;
    switch (currentOrientation) {
      case "down":
        rotate = 0;
        break;
      case "up":
        rotate = 180;
        break;
      case "left":
        rotate = -90;
        break;
      case "right":
        rotate = 90;
        break;
    }
    path.setAttribute(
      "transform",
      rotate === 0 ? "" : `rotate(${rotate} ${centerX} ${centerY})`
    );
  };

  const attach = (): void => {
    svg.setAttribute("class", svgClassName);
    applySize();
    applyPath();
    svg.appendChild(path);
    container.appendChild(svg);

    if (onClick) {
      container.style.cursor = "pointer";
      container.addEventListener("click", onClick);
    }
  };

  attach();

  const api: ChevronAPI = {
    getElement: () => container,
    setOrientation: (newOrientation: ChevronOrientation) => {
      if (isDestroyed) return;
      currentOrientation = newOrientation;
      applyPath();
    },
    setSize: (newWidth: number, newHeight: number) => {
      if (isDestroyed) return;
      currentWidth = newWidth;
      currentHeight = newHeight;
      applySize();
    },
    destroy: () => {
      if (isDestroyed) return;
      isDestroyed = true;
      if (onClick) {
        container.removeEventListener("click", onClick);
      }
      svg.remove();
      container.remove();
    },
  };

  return api;
};

/**
 * Utility to create and append Chevron to container
 */
export const createChevronInContainer = (
  container: HTMLElement,
  config: ChevronConfig = {}
): ChevronAPI => {
  const chevron = createChevron(config);
  container.appendChild(chevron.getElement());
  return chevron;
};
